import { forwardRef, useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import Slide from '@material-ui/core/Slide';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import Chip from '@material-ui/core/Chip';
import Input from '@material-ui/core/Input';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import TextField from '@material-ui/core/TextField';
import { useMediaQuery } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab'
import { useTheme, makeStyles } from '@material-ui/core/styles'
import { useStoreActions } from '../../store'
import { toThaiYear } from '../../utils/thaiDate'
import Swal from 'sweetalert2'

const useStyles = makeStyles((theme) => ({
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
        width: '100%'
    },
    selectEmpty: {
        marginTop: theme.spacing(2),
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    chip: {
        margin: 2,
    },
}));

export default function ExportQuarterReportModal({ open, onClose }) {
    const theme = useTheme()
    const classes = useStyles()
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

    const [year, setYear] = useState(new Date().getFullYear())
    const [quarter, setQuarter] = useState(1)
    const [isOpen, setIsOpen] = useState(false)
    const [eventList, setEventList] = useState([])
    const [operators, setOperators] = useState([])
    const [selectedOperators, setSelectedOperators] = useState([])

    const exportQuarterReport = useStoreActions(a => a.dataSetRecordModel.exportQuarterReportExcel)

    useEffect(() => {
        setIsOpen(open)
    }, [open])

    useEffect(() => {
        if (!open) return

        const fetchEvents = async () => {
            const { form } = require('../../api')
            const res = await form.getEvents()
            if (res && res.data) {
                setEventList(res.data)
                if (res.data.length > 0) {
                    // Try to find active event first
                    const activeEvent = res.data.find(e => e.isActive === 'y')
                    if (activeEvent) {
                        setYear(activeEvent.year)
                        setQuarter(activeEvent.quarter)
                    } else {
                        // Fallback to the first event in the list
                        setYear(res.data[0].year)
                        setQuarter(res.data[0].quarter)
                    }
                }
            }
        }
        fetchEvents()
    }, [open])

    useEffect(() => {
        if (!open) return

        const fetchOperators = async () => {
            const { datasetRecord } = require('../../api')
            try {
                const res = await datasetRecord.getSubmittedOperators(year, quarter)
                if (res && Array.isArray(res)) {
                    // Filter only Mobile operators (qosTypeId == 1)
                    const mobileOperators = res.filter(op => op.qosTypeIds && op.qosTypeIds.includes(1));
                    setOperators(mobileOperators)
                } else {
                    setOperators([])
                }
                setSelectedOperators([]) // Reset selection on year/quarter change
            } catch (error) {
                console.error("Error fetching operators:", error)
            }
        }

        if (year && quarter) {
            fetchOperators()
        }
    }, [year, quarter, open])


    const handleExport = async () => {
        try {
            // If specific operators are selected (max 5), send them.
            // If none are selected, it might imply "all" or just standard export.
            // Requirement says: "add selection of operators... but have maximun selected at 5 operator"
            // And "send this list to api"

            // If user selects nothing, should we send empty list? Original behavior was no list.
            // Assuming if selectedOperators > 0, we send them.

            const payload = {
                Year: year,
                Quarter: quarter,
                OperatorIds: selectedOperators // API expects this if we want to filter
            }

            await exportQuarterReport(payload)
            onClose()
        } catch (error) {
            console.error("Export failed", error)
        }
    }

    const handleOperatorChange = (event) => {
        const value = event.target.value
        if (value.length > 5) {
            // Optional: Show warning or just don't select
            // Swal.fire('Warning', 'เลือกได้สูงสุด 5 ผู้ประกอบการ', 'warning')
            return;
        }
        setSelectedOperators(value);
    };

    // derived state for available years
    const availableYears = [...new Set(eventList.map(item => item.year))].sort((a, b) => b - a)

    // derived state for available quarters based on selected year
    const availableQuarters = eventList
        .filter(ev => ev.year === year)
        .map(ev => ev.quarter)
        .sort((a, b) => a - b)

    return (
        <Dialog
            className="w-full"
            open={isOpen}
            maxWidth="sm"
            fullWidth
            fullScreen={fullScreen}
            TransitionComponent={Transition}
            PaperProps={{
                style: { minHeight: '450px' }
            }}
            onClose={onClose}
            scroll="paper">
            <DialogContent>
                <DialogContentText
                    className="gap-y-5 flex flex-col"
                    tabIndex={-1}
                    component="div">
                    <Grid className="text-lg lg:text-xl text-center font-bold">รายงานประจำไตรมาส</Grid>

                    <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel id="year-select-label">ปี</InputLabel>
                        <Select
                            labelId="year-select-label"
                            value={year}
                            onChange={(e) => {
                                const newYear = e.target.value
                                setYear(newYear)
                                // Reset quarter if current selected quarter is not available in new year
                                const quartersInNewYear = eventList.filter(ev => ev.year === newYear).map(ev => ev.quarter)
                                if (quartersInNewYear.length > 0 && !quartersInNewYear.includes(quarter)) {
                                    setQuarter(quartersInNewYear[0])
                                }
                            }}
                            label="Year"
                        >
                            {availableYears.map((y) => (
                                <MenuItem key={y} value={y}>{toThaiYear(y)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel id="quarter-select-label">ไตรมาส</InputLabel>
                        <Select
                            labelId="quarter-select-label"
                            value={quarter}
                            onChange={(e) => setQuarter(e.target.value)}
                            label="ไตรมาส"
                        >
                            {availableQuarters.map((q) => (
                                <MenuItem key={q} value={q}>ไตรมาส {q}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <div className={classes.formControl}>
                        <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                            เลือกผู้ประกอบการในการเปรียบเทียบข้อมูล (Chart)
                        </div>
                        <Autocomplete
                            multiple
                            id="operator-select"
                            options={operators}
                            getOptionLabel={(option) => option.operatorName || ""}
                            value={selectedOperators.map(id => operators.find(op => op.id === id)).filter(Boolean)}
                            onChange={(event, newValue) => {
                                if (newValue.length > 5) {
                                    // Optional warning, but just limit to 5
                                    return;
                                }
                                setSelectedOperators(newValue.map(op => op.id));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="ผู้ประกอบการ (สูงสุด 5 ราย)"
                                    placeholder="เลือกผู้ประกอบการ"
                                />
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        label={option.operatorName}
                                        {...getTagProps({ index })}
                                        className={classes.chip}
                                    />
                                ))
                            }
                        />
                    </div>

                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    component="div"
                    spacing={2}>
                    <Grid item className="p-3 w-5/12" component="div">
                        <Button
                            fullWidth
                            onClick={onClose}
                            color="default"
                            size="large"
                            variant="contained">
                            ยกเลิก
                        </Button>
                    </Grid>
                    <Grid item className="p-3 w-5/12" component="div">
                        <Button
                            fullWidth
                            onClick={handleExport}
                            color="primary"
                            size="large"
                            variant="contained">
                            ออกรายงาน
                        </Button>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    )
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const Transition = forwardRef(function Transtition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />
})
